import React, { useEffect, useRef } from 'react';
import { registerAsWebComponent } from 'react-webcomponentify';
import {
    myWalletCalculatePaymentsCTAClick,
    myWalletKbbTradeInIndicator,
    myWalletGridTradeInIndicator,
    myWalletSummaryLinkClick,
    walletGridImpression,
} from 'reaxl-analytics';

import BonnetWebcomponent from '@bonnet/webcomponent';
import { paymentsDuck } from '@syndicated-wallet/ducks';
import flags from '@syndicated-wallet/constants/flags';
import { getFeatureFlags } from '@syndicated-wallet/helpers';
import { BrowserAgent } from '@newrelic/browser-agent';
import { logger } from '@atc/syndicated-logging';
import WalletGrid from './WalletGrid';
import getStylesheets from '../themes/getStylesheets';

logger.configure({ level: logger.logLevel.DEBUG });
const walletGridLogger = logger.getLogger('WalletGrid');

const analytics = {
    dataLayer: () => ({
        catalog: 'atc',
    }),
    getDataIsland: () => ({
        page: {
            // BIRF: {
            //     pg: 'test',
            //     pg_id: 'test',
            // },
        },
    }),
    value: {
        myWalletCalculatePaymentsCTAClick,
        myWalletSummaryLinkClick,
        myWalletKbbTradeInIndicator,
        myWalletGridTradeInIndicator,
        walletGridImpression,
    },
    option: {
        isOptimizelyEdge: false,
        disableGTM: false,
        disableGTMEvents: false,
        customBrand: 'myWallet',
        disablePixall: true,
        disableAdobe: true,
    },
};

function loadNewRelicScript(shadowRoot, callback) {
    if (shadowRoot.querySelector('script[id="newrelic_wallet_grid"]')) {
        walletGridLogger.debug('New Relic script already loaded in this component');
        if (callback) callback();
        return;
    }

    const script = document.createElement('script');
    script.id = 'newrelic_wallet_grid';
    script.type = 'text/javascript';
    script.src = process.env.WALLET_GRID_NR_SCRIPT_PATH;
    script.async = true;
    script.onload = () => {
        walletGridLogger.debug('New Relic script loaded successfully.');
        if (callback) callback();
    };

    script.onerror = (error) => {
        walletGridLogger.debug('Failed to load New Relic script:', error);
    };

    try {
        shadowRoot.appendChild(script);
    } catch (error) {
        walletGridLogger.debug('Error appending script:', error);
    }
}

export default function WalletGridWebComponent(props = {}) {
    const { 'data-feature-flags': dataFeatureFlags = '', theme, ...rest } = props;
    const allFeatureFlags = getFeatureFlags({ ...flags }, dataFeatureFlags);
    const componentRef = useRef(null);

    const reducers = [paymentsDuck];
    const iconFontPath = [process.env.S3_WWW_PATH, 'assets', 'fonts'].filter(Boolean).join('/');
    const themePath = [process.env.S3_WWW_PATH, 'assets'].filter(Boolean).join('/');

    useEffect(() => {
        const MAX_RETRIES = 10;
        let retryCount = 0;
        const initNewRelic = () => {
            const webComponent = document.querySelector('wallet-grid');

            if (!webComponent || !webComponent.shadowRoot) {
                retryCount += 1;
                if (retryCount >= MAX_RETRIES) {
                    walletGridLogger.debug(`Failed to initialize New Relic after ${MAX_RETRIES} attempts`);
                    return;
                }
                walletGridLogger.debug('Web component or shadow root not ready, retrying...');
                setTimeout(initNewRelic, 100);
                return;
            }

            loadNewRelicScript(webComponent.shadowRoot, () => {
                const newRelicAgent = new BrowserAgent({
                    isolatedAgent: true,
                    componentName: 'wallet-grid',
                });
                newRelicAgent.start();
                newRelicAgent.addPageAction('ComponentLoaded', {
                    component: 'WalletGrid',
                });
            });
        };

        initNewRelic();

        return () => {};
    }, []);

    return (
        <BonnetWebcomponent
            ref={componentRef}
            iconFontPath={iconFontPath}
            stylesheets={getStylesheets({ theme, themePath })}
            reducers={reducers}
            analytics={analytics}
            flags={{
                config: allFeatureFlags,
            }}
        >
            <WalletGrid {...rest} />
        </BonnetWebcomponent>
    );
}

registerAsWebComponent(WalletGridWebComponent, 'wallet-grid', 'open');
